import React from "react";
import { useNavigate } from "react-router-dom";
import WorldMap from "../components/learn/maps/WorldMap";
import useLearnProgress from "../hooks/useLearnProgress";
import { useLearnCloudNavigate } from "../components/learn/PixleyCloudTransition";
import { LEARN } from "../components/learn/theme";
import { LEARN_UI } from "../components/learn/media/learnAssets";
import { WRITING_HORIZONTAL_FRACTIONS, WRITING_TOWN_LESSONS } from "../data/writingTownCatalog";
import {
  firstUnfinishedWritingActivity, isWritingTownUnlocked, writingLessonRoute, writingTownNodeState,
} from "../utils/learnWritingProgress";

const T = LEARN.course.writing;
const ART_ASPECT = 1024 / 3072;

/**
 * Writing Town — a path that climbs from the first stop at the bottom to the
 * Author Studio at the top. The page itself scrolls (no nested scroll trap);
 * the info rail and Pip stay put while the path moves.
 */
export default function WritingTownPage() {
  const navigate = useNavigate();
  const { navigateWithCloud } = useLearnCloudNavigate();
  const { writingTownSet, nextWritingTownLesson, writingDone, writingFinished, courses } = useLearnProgress();

  const open = (lesson) => {
    if (!isWritingTownUnlocked(lesson.townId, writingTownSet)) return;
    const resume = firstUnfinishedWritingActivity(lesson, courses);
    navigateWithCloud(writingLessonRoute(lesson.townId, resume.id));
  };

  const count = WRITING_TOWN_LESSONS.length;
  const nodes = WRITING_TOWN_LESSONS.map((lesson, i) => {
    // Stop 1 sits at the bottom of the art; the last stop just under the signpost.
    const visual = count - 1 - i;
    return {
      id: lesson.townId,
      label: String(lesson.townNumber),
      caption: lesson.title,
      sub: lesson.subtitle,
      state: writingTownNodeState(lesson.townId, writingTownSet),
      x: WRITING_HORIZONTAL_FRACTIONS[visual] ?? 0.5,
      y: 0.93 - i * (0.80 / (count - 1)),
      onSelect: () => open(lesson),
    };
  });

  const next = nextWritingTownLesson;

  return (
    <WorldMap
      eyebrow="Writing"
      title="Writing Town"
      artKey="writing_town_path_bg"
      artAspect={ART_ASPECT}
      accent={T.accent}
      accentDeep={T.deep}
      nodes={nodes}
      nodeScale={0.15}
      scrollable
      scrollToNodeId={next?.townId || WRITING_TOWN_LESSONS[count - 1].townId}
      progress={{ done: writingDone, total: count }}
      next={next
        ? { title: next.title, subtitle: `Stop ${next.townNumber} · ${next.subtitle}`, onStart: () => open(next), cta: writingDone === 0 ? "Start" : "Continue" }
        : { title: "Writing Town is glowing!", subtitle: "Every stop is ready to visit again.", onStart: () => open(WRITING_TOWN_LESSONS[0]), cta: "Play again" }}
      onBack={() => navigate("/learn")}
      backLabel="Pixley Learn"
      companionPose={writingFinished ? "cheer" : "idle"}
      tip={next ? "Climb the path, one stop at a time." : undefined}
    >
      {/* The Author Studio signpost at the summit */}
      {({ wide, compact }) => (
        <img
          src={LEARN_UI("map_signpost")}
          alt=""
          aria-hidden="true"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={wide
            ? { position: "absolute", left: "74%", top: "2%", width: "11%", maxWidth: 150, filter: "drop-shadow(0 8px 14px rgba(11,26,51,0.25))", pointerEvents: "none", zIndex: 1 }
            : { position: "absolute", left: "50%", top: compact ? "5%" : "2.2%", transform: "translateX(-50%)", width: "34%", maxWidth: 190, filter: "drop-shadow(0 8px 14px rgba(11,26,51,0.25))", pointerEvents: "none", zIndex: 1 }}
        />
      )}
    </WorldMap>
  );
}
